import { Auth } from '@aws-amplify/auth';
import { CognitoRefreshToken, CognitoUser, CognitoUserAttribute, CognitoUserSession } from 'amazon-cognito-identity-js';
import { logger } from './error-tracker';

/**
 * `revokeTokens` is part of CognitoUser's runtime surface but is missing from
 * the published typings (see amazon-cognito-identity-js CognitoUser.js), so it
 * is declared here rather than reached for with an `any`.
 */
type RevocableCognitoUser = CognitoUser & {
  revokeTokens(callback: (error?: Error) => void): void;
};

/**
 * Messages Cognito returns when the session is already gone. At logout these
 * are benign - there is nothing left to end. Every other failure leaves the
 * refresh token live, so it must not be quietly treated the same way.
 */
const SESSION_ALREADY_ENDED = [
  'Access Token has been revoked',
  'Refresh Token has been revoked',
  'Refresh Token has expired',
  'User does not exist.',
  'User is disabled.',
  'The user is not authenticated',
  'No current user',
];

function isSessionAlreadyEnded(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return SESSION_ALREADY_ENDED.some((known) => message.includes(known));
}

/**
 * Reports a failure to end the session, and never throws.
 *
 * `logger.logException` rethrows anything Sentry raises other than rate
 * limiting, and this runs on the path to the local sign-out. Letting that
 * escape would leave the tokens sitting on the device with the user shown as
 * signed out, which is worse than the failure being reported.
 */
function reportFailureToEndSession(error: unknown): void {
  if (isSessionAlreadyEnded(error)) {
    return;
  }

  try {
    logger.logException(error instanceof Error ? error : new Error(String(error)));
  } catch {
    // Nothing useful is left to do: reporting is what just failed.
  }
}

/**
 * Ends this session on the Cognito side (PE-1848).
 *
 * Prefers revoking just this session's tokens, which leaves the user's other
 * devices signed in - a teacher logging out on a laptop should not drop the
 * classroom smartboard mid-lesson. Revoking the refresh token also revokes the
 * access tokens issued alongside it, so the browser cannot rehydrate from
 * re-imported cookies either.
 */
async function endServerSideSession(): Promise<void> {
  const user = (await Auth.currentAuthenticatedUser()) as RevocableCognitoUser;
  const accessToken = user.getSignInUserSession()?.getAccessToken();
  // Cognito only stamps origin_jti when the app client has token revocation
  // enabled. Without it RevokeToken is rejected outright, and Cognito's own
  // client library silently skips revocation rather than failing - so read the
  // claim instead of assuming, and end every session when it is absent.
  const canRevokeThisSession = Boolean(accessToken?.decodePayload()?.origin_jti);

  if (canRevokeThisSession) {
    await new Promise<void>((resolve, reject) => {
      user.revokeTokens((error) => (error ? reject(error) : resolve()));
    });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    user.globalSignOut({ onSuccess: () => resolve(), onFailure: reject });
  });
}

export const cognitoAuth = {
  signIn(username: string, password: string) {
    return Auth.signIn(username, password);
  },

  signUp(input: Parameters<typeof Auth.signUp>[0]) {
    return Auth.signUp(input);
  },

  resendSignUp(username: string) {
    return Auth.resendSignUp(username);
  },

  confirmSignUp(username: string, code: string) {
    return Auth.confirmSignUp(username, code);
  },

  currentSession() {
    return Auth.currentSession();
  },

  currentAuthenticatedUser() {
    return Auth.currentAuthenticatedUser();
  },

  completeNewPassword(user: any, newPassword: string, requiredAttributes: any) {
    return Auth.completeNewPassword(user, newPassword, requiredAttributes);
  },

  changePassword(currentUser: any, oldPassword: string, newPassword: string) {
    return Auth.changePassword(currentUser, oldPassword, newPassword);
  },

  forgotPassword(username: string) {
    return Auth.forgotPassword(username);
  },

  forgotPasswordSubmit(username: string, verificationCode: string, newPassword: string) {
    return Auth.forgotPasswordSubmit(username, verificationCode, newPassword);
  },

  /**
   * Ends the session server side, then clears it locally (PE-1848).
   *
   * A local sign-out on its own only deletes the client-side tokens, leaving
   * the refresh token live for the rest of its validity (1 day on the US app
   * clients, 30 on FRA), so re-importing the cookies into a clean browser
   * restores the session. There is deliberately no option to skip the
   * server-side step.
   */
  async signOut() {
    try {
      await endServerSideSession();
    } catch (error) {
      // Always fall through to the local sign-out: leaving tokens on the device
      // is worse than a server-side session we could not end. But only an
      // already-ended session is benign - a network failure or a Cognito outage
      // leaves the refresh token live, which is this exact finding reopening,
      // so report that rather than logging it to a console nobody reads.
      reportFailureToEndSession(error);
    }

    return Auth.signOut();
  },

  getUserAttributes(cognitoUser: CognitoUser): Promise<CognitoUserAttribute[]> {
    return new Promise((resolve, reject) => {
      cognitoUser.getUserAttributes((error, attributes) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(attributes ?? []);
      });
    });
  },

  refreshSession(cognitoUser: CognitoUser, refreshToken: CognitoRefreshToken): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(refreshToken, (error, session) => {
        if (error) {
          reject(error);
          return;
        }

        if (!session) {
          reject(new Error('Cognito refreshSession completed without a session'));
          return;
        }

        resolve(session);
      });
    });
  },
};
